import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  // Chip,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import { alpha, useTheme } from '@mui/material/styles';
import { SectionHeader } from '../layout/SectionHeader';
import { textScale } from '../../theme/standards';

interface SpecialActivity {
  special_id: string;
  title: string;
  snippet: string;
  url: string;
  event_date?: string;
  categories: string[];
  image_url?: string;
  city: string;
  country: string;
  source?: string;
  score?: number;
}

interface SpecialActivitiesSectionProps {
  activitiesByDay: Record<string, SpecialActivity[]>;
}

const SpecialActivityCard: React.FC<{ activity: SpecialActivity }> = ({ activity }) => {
  const theme = useTheme();
  const cardRadius = 8;
  const accentColor = theme.palette.secondary.main;
  const defaultBorder = `1px solid ${alpha(theme.palette.primary.main, 0.12)}`;
  const imageFallbackGradient = `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.7)} 0%, ${alpha(accentColor, 0.55)} 100%)`;

  const categoryPillSx = {
    display: 'inline-flex',
    alignItems: 'center',
    px: 1.25,
    py: 0.35,
    borderRadius: '999px',
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: textScale.support.fontSize,
    lineHeight: 1.3,
    letterSpacing: '0.04em',
  } as const;

  const metaPillSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    px: 1.25,
    py: 0.45,
    borderRadius: '999px',
    backgroundColor: alpha(theme.palette.secondary.main, 0.12),
    color: theme.palette.text.primary,
  } as const;

  const metaIconSx = {
    fontSize: 16,
    color: theme.palette.secondary.main,
  } as const;

  const metaLabelSx = {
    fontWeight: 600,
    fontSize: textScale.body.fontSize,
    color: theme.palette.text.primary,
  } as const;

  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    
    // Handle date ranges (e.g., "2025-05-08 to 2025-11-02")
    if (dateString.includes(' to ')) {
      const [startDate, endDate] = dateString.split(' to ').map(d => d.trim());
      try {
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        // Check if dates are valid
        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
          return dateString; // Return original string if parsing fails
        }
        
        const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
        const formattedStart = start.toLocaleDateString('en-US', options);
        const formattedEnd = end.toLocaleDateString('en-US', options);
        
        // If same year, show year only once
        if (start.getFullYear() === end.getFullYear()) {
          const startWithoutYear = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          return `${startWithoutYear} - ${formattedEnd}`;
        }
        
        return `${formattedStart} - ${formattedEnd}`;
      } catch {
        return dateString;
      }
    }
    
    // Handle single date
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Return original string if invalid
      }
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const handleCardClick = () => {
    window.open(activity.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: `${cardRadius}px`,
        border: defaultBorder,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
          transform: 'translateY(-4px)',
          border: `1px solid ${alpha(accentColor, 0.3)}`,
        },
      }}
    >
      <CardActionArea onClick={handleCardClick} sx={{ height: '100%' }}>
        {activity.image_url && (
          <Box
            sx={{
              width: '100%',
              height: 180,
              backgroundImage: `url(${activity.image_url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        )}
        {!activity.image_url && (
          <Box
            sx={{
              width: '100%',
              height: 180,
              background: imageFallbackGradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LanguageRoundedIcon sx={{ fontSize: 64, color: 'white', opacity: 0.8 }} />
          </Box>
        )}

        <CardContent sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3, flex: 1 }}>
                {activity.title}
              </Typography>
              <IconButton
                size="small"
                sx={{
                  backgroundColor: alpha(accentColor, 0.12),
                  color: accentColor,
                  '&:hover': {
                    backgroundColor: alpha(accentColor, 0.2),
                  },
                }}
              >
                <OpenInNewRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                lineHeight: 1.6,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {activity.snippet}
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {activity.event_date && (
                <Box sx={metaPillSx}>
                  <EventRoundedIcon sx={metaIconSx} />
                  <Typography sx={metaLabelSx}>{formatDate(activity.event_date)}</Typography>
                </Box>
              )}
              {activity.categories.slice(0, 2).map((category, idx) => (
                <Box key={idx} sx={categoryPillSx}>
                  {category}
                </Box>
              ))}
            </Stack>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pt: 1,
                borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
              }}
            >
              <LanguageRoundedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: textScale.support.fontSize }}>
                From the web
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export const SpecialActivitiesSection: React.FC<SpecialActivitiesSectionProps> = ({ activitiesByDay }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _theme = useTheme();
  
  // Flatten all activities from all days
  const allActivities = Object.entries(activitiesByDay).flatMap(([date, activities]) =>
    activities.map(activity => ({ ...activity, displayDate: date }))
  );

  if (allActivities.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <SectionHeader
        eyebrow="Special Events"
        title="From the Web"
        subtitle="Real events, concerts, festivals and special activities happening during your visit"
      />
      <Box
        sx={{
          display: 'grid',
          gap: { xs: 3, md: 4 },
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, minmax(0, 1fr))',
            xl: 'repeat(3, minmax(0, 1fr))',
          },
        }}
      >
        {allActivities.map((activity) => (
          <SpecialActivityCard key={activity.special_id} activity={activity} />
        ))}
      </Box>
    </Box>
  );
};
